import React from 'react';
import { DeviceLogEntry } from './deviceLogs';

interface Props {
  entries: DeviceLogEntry[];
}

/** Aggregate facts for the loaded session, computed client-side (Plan 104). */
export function summarizeSession(entries: DeviceLogEntry[]) {
  const first = entries[0];
  const firstDevice = entries.find((e) => e.deviceName)?.deviceName ?? null;
  let errors = 0;
  let warns = 0;
  let credentialChanges = 0;
  let lastResult: string | null = null;
  for (const e of entries) {
    if (e.level === 'error') errors += 1;
    if (e.level === 'warn') warns += 1;
    if (e.ctx?.passwordChanged === true) credentialChanges += 1;
    if (typeof e.ctx?.result === 'string') lastResult = e.ctx.result as string;
  }
  return {
    platform: first?.platform ?? null,
    appVersion: first?.appVersion ?? null,
    userId: first?.userId ?? null,
    firstDevice,
    errors,
    warns,
    credentialChanges,
    lastResult,
    count: entries.length,
  };
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <span className="inline-flex items-baseline gap-1 mr-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-xs font-mono text-gray-800">{value}</span>
    </span>
  );
}

const SessionFacts: React.FC<Props> = ({ entries }) => {
  if (entries.length === 0) return null;
  const s = summarizeSession(entries);
  return (
    <div className="mb-2 px-2 py-1.5 bg-gray-50 border border-border rounded-sm text-xs leading-6">
      <Fact label="app" value={s.platform && s.appVersion ? `${s.platform} ${s.appVersion}` : s.appVersion} />
      <Fact label="user" value={s.userId} />
      <Fact label="device" value={s.firstDevice ?? '—'} />
      <Fact label="entries" value={s.count} />
      <Fact
        label="errors"
        value={
          <span className={s.errors > 0 ? 'text-red-700 font-semibold' : undefined}>{s.errors}</span>
        }
      />
      <Fact
        label="warns"
        value={
          <span className={s.warns > 0 ? 'text-status-warning-foreground font-semibold' : undefined}>{s.warns}</span>
        }
      />
      {s.credentialChanges > 0 && <Fact label="pw changes" value={s.credentialChanges} />}
      <Fact label="last result" value={s.lastResult} />
    </div>
  );
};

export default SessionFacts;
